/**
 * Removes empty attributes from an object recursively
 * @param object - The object to remove empty attributes from
 * @returns The cleaned object with empty attributes removed
 */
export function removeEmptyAttributes<T extends Record<string, any>>(object: T): T {
	Object.entries(object).forEach(([key, value]) => {
		if (value && typeof value === "object") {
			removeEmptyAttributes(value)
		}
		if ((value && typeof value === "object" && !Object.keys(value).length) || value === "") {
			if (Array.isArray(object)) {
				object.splice(parseInt(key), 1)
			} else {
				delete object[key]
			}
		}
	})
	return object
}

/**
 * Get a nested value from an object using a path string
 * @param obj - The object to retrieve the value from
 * @param path - The path to the value (e.g., "user.name", "items[0].id")
 * @returns The value at the specified path or undefined if not found
 * @example
 * // Returns 'John'
 * getNestedValue({ user: { name: 'John' } }, 'user.name')
 *
 * // Returns 'first'
 * getNestedValue({ items: [{ id: 'first' }, { id: 'second' }] }, 'items[0].id')
 */
export function getNestedValue<T = any>(obj: Record<string, any>, path: string): T | undefined {
	const parts = path.split(".")
	let current = obj

	for (let i = 0; i < parts.length; i++) {
		const part = parts[i]
		const arrayMatch = part.match(/(\w+)\[(\d+)\]/)

		if (arrayMatch) {
			const [, name, index] = arrayMatch
			const idx = parseInt(index, 10)
			if (!current[name] || !Array.isArray(current[name]) || !current[name][idx]) {
				return undefined
			}
			current = current[name][idx]
		} else if (current[part] !== undefined) {
			current = current[part]
		} else {
			return undefined
		}
	}

	return current as T
}

/**
 * Set a nested value in an object using a path string
 * @param obj - The object to set the value in
 * @param path - The path to the value (e.g., "user.name", "items[0].id")
 * @param value - The value to set at the specified path
 * @returns A new object with the updated value (original object is not modified)
 * @example
 * // Returns { user: { name: 'Jane' } }
 * setNestedValue({ user: { name: 'John' } }, 'user.name', 'Jane')
 *
 * // Returns { items: [{ id: 'updated' }, { id: 'second' }] }
 * setNestedValue({ items: [{ id: 'first' }, { id: 'second' }] }, 'items[0].id', 'updated')
 */
export function setNestedValue<T extends Record<string, any>, V>(obj: T, path: string, value: V): T {
	// Create a copy that we can safely modify
	const result = {...obj} as Record<string, any>
	const parts = path.split(".")
	let current = result

	for (let i = 0; i < parts.length; i++) {
		const part = parts[i]
		const arrayMatch = part.match(/(\w+)\[(\d+)\]/)

		if (arrayMatch) {
			const [, name, index] = arrayMatch
			const idx = parseInt(index, 10)

			if (i === parts.length - 1) {
				// Last part, set the value
				if (!current[name]) {
					current[name] = []
				}

				while (current[name].length <= idx) {
					current[name].push(undefined)
				}

				current[name][idx] = value
			} else {
				// Not the last part, create the path
				if (!current[name]) {
					current[name] = []
				}

				while (current[name].length <= idx) {
					current[name].push({})
				}

				if (typeof current[name][idx] !== "object") {
					current[name][idx] = {}
				}

				current = current[name][idx]
			}
		} else {
			if (i === parts.length - 1) {
				// Last part, set the value
				current[part] = value
			} else {
				// Not the last part, create the path
				if (!current[part] || typeof current[part] !== "object") {
					current[part] = {}
				}
				current = current[part]
			}
		}
	}

	return result as T
}

/**
 * Checks if a record-like object is "empty" in a shallow way.
 *
 * A record is considered empty if:
 * - It is null or undefined
 * - It has no own enumerable properties
 * - Every property value is either:
 *   - null or undefined
 *   - a string that is empty or whitespace-only
 *
 * Any other value (numbers, booleans, objects, arrays, functions, etc.) makes the record non-empty.
 *
 * Notes:
 * - This function only accepts plain key–value objects (`Record<string, unknown>`).
 * - Symbol-keyed properties are ignored.
 * - This check is shallow: nested objects/arrays are not traversed and will count as non-empty.
 *
 * @param record - The record to check
 * @returns true if the record is shallow-empty; false otherwise
 *
 * @example
 * isRecordEmpty(null)                         // true
 * isRecordEmpty({})                           // true
 * isRecordEmpty({ a: "" })                    // true
 * isRecordEmpty({ a: " " })                   // true
 * isRecordEmpty({ a: "   ", b: undefined })   // true
 * isRecordEmpty({ a: 0 })                     // false
 * isRecordEmpty({ a: {} })                    // false (nested objects are not traversed)
 */
export function isRecordEmpty(record?: Record<string, unknown> | null): boolean {
	if (record == null) return true

	const values = Object.values(record)
	if (values.length === 0) return true

	return values.every((value) => {
		if (value == null) return true // null/undefined
		if (typeof value === "string") return value.trim() === ""
		return false // numbers, booleans, objects, arrays, functions → non-empty
	})
}
